import { useEffect, useMemo, useRef, useState } from 'react';
import { apiJson } from '../api/client.js';
import { useAuth } from '../context/AuthContext.jsx';

const GROUPS = ['account', 'notifications', 'time_tracker', 'forms'];

function draftFrom(template) {
  const variables = {};
  for (const variable of template.variables || []) {
    variables[variable.key] = variable.sample || '';
  }
  return {
    subject: template.subject || '',
    body: template.body || '',
    variables,
  };
}

function insertToken(element, value, token, setValue) {
  const start = element?.selectionStart ?? value.length;
  const end = element?.selectionEnd ?? value.length;
  const next = `${value.slice(0, start)}${token}${value.slice(end)}`;
  setValue(next);
  const cursor = start + token.length;
  requestAnimationFrame(() => {
    if (!element) return;
    element.focus();
    element.setSelectionRange(cursor, cursor);
  });
}

export function AdminEmailTemplatesPage() {
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [mail, setMail] = useState(null);
  const [selectedKey, setSelectedKey] = useState('');
  const [draft, setDraft] = useState({ subject: '', body: '', variables: {} });
  const [preview, setPreview] = useState(null);
  const [to, setTo] = useState(user?.email || '');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [filter, setFilter] = useState('');
  const subjectRef = useRef(null);
  const bodyRef = useRef(null);
  const focusField = useRef('body');

  const selected = templates.find((item) => item.key === selectedKey) || null;

  const grouped = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    const visible = templates.filter((item) => {
      if (!needle) return true;
      return item.name.toLowerCase().includes(needle)
        || item.description.toLowerCase().includes(needle)
        || item.key.toLowerCase().includes(needle);
    });
    return GROUPS.map((category) => ({
      category,
      label: templates.find((item) => item.category === category)?.category_label || category,
      items: visible.filter((item) => item.category === category),
    })).filter((group) => group.items.length > 0);
  }, [templates, filter]);

  const load = async (preferKey) => {
    const data = await apiJson('/api/admin/email-templates');
    setTemplates(data.templates || []);
    setMail(data.mail || null);
    const nextKey = preferKey || data.templates?.[0]?.key || '';
    setSelectedKey(nextKey);
    const next = (data.templates || []).find((item) => item.key === nextKey);
    if (next) setDraft(draftFrom(next));
    return next;
  };

  useEffect(() => {
    load()
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const choose = (template) => {
    setSelectedKey(template.key);
    setDraft(draftFrom(template));
    setPreview(null);
    setMessage('');
    setError('');
  };

  const updateDraft = (patch) => setDraft((current) => ({ ...current, ...patch }));

  const insertVariable = (key) => {
    const token = `{{${key}}}`;
    if (focusField.current === 'subject') {
      insertToken(subjectRef.current, draft.subject, token, (subject) => updateDraft({ subject }));
      return;
    }
    insertToken(bodyRef.current, draft.body, token, (body) => updateDraft({ body }));
  };

  const previewTemplate = async () => {
    if (!selected) return;
    setBusy('preview');
    setError('');
    try {
      const rendered = await apiJson(`/api/admin/email-templates/${selected.key}/preview`, {
        method: 'POST',
        body: draft,
      });
      setPreview(rendered);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy('');
    }
  };

  const save = async () => {
    if (!selected) return;
    setBusy('save');
    setError('');
    setMessage('');
    try {
      const saved = await apiJson(`/api/admin/email-templates/${selected.key}`, {
        method: 'PUT',
        body: { subject: draft.subject, body: draft.body },
      });
      setTemplates((current) => current.map((item) => (item.key === saved.key ? saved : item)));
      setDraft((current) => ({ ...current, subject: saved.subject, body: saved.body }));
      setMessage('Saved.');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy('');
    }
  };

  const reset = async () => {
    if (!selected) return;
    if (!window.confirm(`Restore the original "${selected.name}" letter?`)) return;
    setBusy('reset');
    setError('');
    setMessage('');
    try {
      const restored = await apiJson(`/api/admin/email-templates/${selected.key}`, { method: 'DELETE' });
      setTemplates((current) => current.map((item) => (item.key === restored.key ? restored : item)));
      setDraft(draftFrom(restored));
      setPreview(null);
      setMessage('Restored the original letter.');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy('');
    }
  };

  const sendTest = async () => {
    if (!selected) return;
    setBusy('test');
    setError('');
    setMessage('');
    try {
      const result = await apiJson(`/api/admin/email-templates/${selected.key}/test`, {
        method: 'POST',
        body: { ...draft, to },
      });
      setMessage(result.message || 'Test email sent.');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy('');
    }
  };

  if (loading) {
    return <p className="muted">Loading…</p>;
  }

  return (
    <div>
      <h2 style={{ marginTop: 0 }} data-tour="page-title">Email templates</h2>
      <p className="muted">
        Letters the portal can send for this agency. A placeholder such as {'{{portal_label}}'} is filled in when the letter goes out.
        A test send uses the sample values below and the text currently in the editor.
      </p>
      {mail ? (
        <p className={mail.ready ? 'muted' : 'err'}>
          Outbound mail: {mail.ready ? `${mail.driver}, from ${mail.from}` : 'not configured. Set MAIL_FROM_ADDRESS before sending a test.'}
        </p>
      ) : null}
      {error ? <p className="err">{error}</p> : null}
      {message ? <p className="muted">{message}</p> : null}

      <div className="email-templates-layout">
        <aside className="card-panel email-templates-nav">
          <input
            className="input email-templates-filter"
            type="search"
            value={filter}
            placeholder="Find a letter"
            onChange={(event) => setFilter(event.target.value)}
          />
          {grouped.map((group) => (
            <div key={group.category}>
              <div className="email-templates-group">{group.label}</div>
              {group.items.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  className={item.key === selectedKey ? 'email-templates-item is-active' : 'email-templates-item'}
                  onClick={() => choose(item)}
                >
                  <span>{item.name}</span>
                  {item.customized ? <span className="email-templates-badge">Edited</span> : null}
                </button>
              ))}
            </div>
          ))}
        </aside>

        {selected ? (
          <section className="card-panel email-templates-editor">
            <h3 style={{ marginTop: 0 }}>{selected.name}</h3>
            <p className="muted">{selected.description}</p>
            {selected.scope === 'starter' ? (
              <p className="muted">This text is copied onto new forms. Open a form to change the letter for that form only.</p>
            ) : null}
            {selected.button ? (
              <p className="muted">
                Button: {selected.button.label}. It uses <code>{`{{${selected.button.variable}}}`}</code>.
              </p>
            ) : null}
            {selected.html ? <p className="muted">This letter accepts HTML in the body.</p> : null}

            <div className="email-templates-variables">
              {(selected.variables || []).map((variable) => (
                <button
                  key={variable.key}
                  type="button"
                  className="email-templates-chip"
                  title={variable.description}
                  onClick={() => insertVariable(variable.key)}
                >
                  {`{{${variable.key}}}`}
                  <span>{variable.label}</span>
                </button>
              ))}
            </div>

            <label className="field">
              <span>Subject</span>
              <input
                ref={subjectRef}
                className="input"
                value={draft.subject}
                onFocus={() => { focusField.current = 'subject'; }}
                onChange={(event) => updateDraft({ subject: event.target.value })}
              />
            </label>
            <label className="field">
              <span>Body</span>
              <textarea
                ref={bodyRef}
                className="input email-templates-body"
                rows={selected.html ? 12 : 8}
                value={draft.body}
                onFocus={() => { focusField.current = 'body'; }}
                onChange={(event) => updateDraft({ body: event.target.value })}
              />
            </label>

            <h4>Sample values</h4>
            <p className="muted small">Preview and the test email fill the variables with these values. They are not saved.</p>
            <div className="email-templates-samples">
              {(selected.variables || []).map((variable) => (
                <label key={variable.key} className="field">
                  <span>{variable.label}</span>
                  <input
                    className="input"
                    value={draft.variables[variable.key] || ''}
                    onChange={(event) => updateDraft({
                      variables: { ...draft.variables, [variable.key]: event.target.value },
                    })}
                  />
                </label>
              ))}
            </div>

            <div className="row-actions">
              <button type="button" className="btn-primary" disabled={busy !== ''} onClick={() => void save()}>
                {busy === 'save' ? 'Saving…' : 'Save'}
              </button>
              <button type="button" className="btn-secondary" disabled={busy !== ''} onClick={() => void previewTemplate()}>
                {busy === 'preview' ? 'Rendering…' : 'Preview'}
              </button>
              <button type="button" className="btn-secondary" disabled={busy !== '' || !selected.customized} onClick={() => void reset()}>
                Restore original
              </button>
            </div>

            <div className="email-templates-test">
              <label className="field">
                <span>Send a test to</span>
                <input className="input" type="email" value={to} onChange={(event) => setTo(event.target.value)} />
              </label>
              <button type="button" className="btn-secondary" disabled={busy !== '' || !mail?.ready} onClick={() => void sendTest()}>
                {busy === 'test' ? 'Sending…' : 'Send test'}
              </button>
            </div>

            {preview ? (
              <div className="email-templates-preview">
                <h4>Preview</h4>
                <p className="muted">Subject: {preview.subject}</p>
                <iframe title="Email preview" sandbox="" srcDoc={preview.html} />
              </div>
            ) : null}

            {selected.events?.length ? (
              <div className="email-templates-events">
                <h4>Alerts that use this letter</h4>
                <p className="muted small">
                  Each alert writes its own title and message into {'{{title}}'} and {'{{body}}'}.
                  People still choose email or in-app delivery in their profile.
                </p>
                <ul>
                  {selected.events.map((event) => (
                    <li key={event.key}>
                      <strong>{event.label}</strong>
                      <span className="muted small"> · {event.audience}</span>
                      <div className="muted small">{event.description}</div>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </section>
        ) : (
          <p className="muted">No letters are registered.</p>
        )}
      </div>
    </div>
  );
}
